import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import CheckIcon from '@mui/icons-material/Check'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined'
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import { BoxProps, IconButton, ListItemButton, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { AppBlack, AppGreen, AppTextDisabled, BorderColor } from 'appconstants/AppColors'
import { ReactComponent as CircleGrayCheck } from 'assets/circle_gray_check.svg'
import { ReactComponent as CircleGreenCheck } from 'assets/circle_green_check.svg'
import { StepModel } from 'models/LeadStateModel'
import { Pages } from 'routes/types'

type StepProps = BoxProps & {
  data?: StepModel
  isLast?: boolean
  isMalaysiaStyle?: boolean
  totalSteps?: number
  onItemClick: (data: StepModel) => void
}

const malaysiaStepIconMap = {
  [Pages.CreateLead]: AssignmentOutlinedIcon,
  [Pages.UenDetails]: DescriptionOutlinedIcon,
  [Pages.PanDetail]: DescriptionOutlinedIcon,
  [Pages.BusinessDetails]: StorefrontOutlinedIcon,
  [Pages.BusinessDetail]: StorefrontOutlinedIcon,
  [Pages.MultiStores]: StoreOutlinedIcon,
  [Pages.BankAccount]: AccountBalanceOutlinedIcon,
  [Pages.Products]: Inventory2OutlinedIcon,
  [Pages.PricingPlan]: RequestQuoteOutlinedIcon,
  [Pages.KycDetails]: BadgeOutlinedIcon,
  [Pages.PartnershipKyc]: BadgeOutlinedIcon,
  [Pages.CapturePhoto]: PhotoCameraOutlinedIcon,
  [Pages.ReviewDetails]: ChecklistOutlinedIcon,
  [Pages.PurchaseOrder]: ReceiptLongOutlinedIcon,
  [Pages.POPayment]: PaymentsOutlinedIcon,
}

export const Step: React.FC<StepProps> = ({
  data,
  isLast,
  isMalaysiaStyle = false,
  totalSteps = 0,
  onItemClick,
  ...boxProps
}) => {
  const activeColor = isMalaysiaStyle ? '#4B662C' : AppGreen
  const activeTextColor = isMalaysiaStyle ? '#171D1B' : AppBlack
  const inactiveTextColor = isMalaysiaStyle ? '#6F756E' : '#696C74'
  const connectorColor = isMalaysiaStyle ? '#E0E4E0' : BorderColor
  const malaysiaFont = '"Inter", "Roboto", sans-serif'
  const isCompleted = data.status === 'completed'
  const isCurrent = data.status === 'inprogress'
  const isDisabled = data.status === 'disabled'
  const isClickable = isCompleted || isCurrent

  if (isMalaysiaStyle) {
    const MalaysiaIcon = malaysiaStepIconMap[data.type] || AssignmentOutlinedIcon
    const iconColor = isDisabled ? '#9AA39B' : activeColor
    const iconBackground = isCompleted ? '#D2EC9F' : '#FFFFFF'
    const stepTotal = totalSteps || data.order

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mb: isLast ? 0 : '14px',
          position: 'relative',
          ...boxProps.sx,
        }}
      >
        {!isLast && (
          <Box
            sx={{
              position: 'absolute',
              left: '17px',
              top: '45px',
              bottom: '-30px',
              width: '1px',
              backgroundColor: connectorColor,
              zIndex: 0,
            }}
          />
        )}

        <ListItemButton
          onClick={() => {
            if (isClickable) onItemClick(data)
          }}
          disabled={!isClickable}
          sx={{
            alignItems: 'center',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            minHeight: isCurrent ? '68px' : '60px',
            opacity: 1,
            p: isCurrent ? '10px 8px' : '8px 0',
            position: 'relative',
            zIndex: 1,
            backgroundColor: isCurrent ? '#E9EFEC' : 'transparent',
            '&.Mui-disabled': {
              opacity: 1,
            },
            '&:hover': {
              backgroundColor: isCurrent ? '#E9EFEC' : 'rgba(65, 73, 66, 0.08)',
            },
          }}
        >
          <Box sx={{ alignItems: 'center', display: 'flex', gap: '8px', minWidth: 0 }}>
            <Box
              sx={{
                alignItems: 'center',
                backgroundColor: iconBackground,
                border: isCompleted ? 'none' : '1px solid #E3EAE6',
                borderRadius: '28px',
                display: 'flex',
                height: '35px',
                justifyContent: 'center',
                position: 'relative',
                width: '35px',
                zIndex: 2,
              }}
            >
              <MalaysiaIcon sx={{ color: iconColor, fontSize: 22 }} />
              {isCompleted && (
                <Box
                  sx={{
                    alignItems: 'center',
                    backgroundColor: activeColor,
                    border: '2px solid #FFFFFF',
                    borderRadius: '50%',
                    bottom: '-2px',
                    display: 'flex',
                    height: '15px',
                    justifyContent: 'center',
                    position: 'absolute',
                    right: '-2px',
                    width: '15px',
                  }}
                >
                  <CheckIcon sx={{ color: '#FFFFFF', fontSize: 10 }} />
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#414942',
                  fontFamily: malaysiaFont,
                  fontSize: '12px',
                  fontWeight: 400,
                  letterSpacing: '0.4px',
                  lineHeight: '16px',
                }}
              >
                {`Step ${data.order} of ${stepTotal}`}
              </Typography>
              <Typography
                noWrap
                sx={{
                  color: isDisabled ? inactiveTextColor : activeTextColor,
                  fontFamily: malaysiaFont,
                  fontSize: '16px',
                  fontWeight: 400,
                  letterSpacing: '0.5px',
                  lineHeight: '24px',
                  maxWidth: { xs: '200px', md: '154px' },
                }}
              >
                {data.label}
              </Typography>
            </Box>
          </Box>

          <IconButton
            aria-label={
              isCompleted
                ? `Edit ${data.label}`
                : isCurrent
                  ? `Continue ${data.label}`
                  : `${data.label} unavailable`
            }
            disabled={!isClickable}
            onClick={(event) => {
              event.stopPropagation()
              if (isClickable) onItemClick(data)
            }}
            sx={{
              backgroundColor: isCurrent ? activeColor : isCompleted ? '#E9EFEC' : '#E8E8EA',
              borderRadius: '100px',
              flexShrink: 0,
              height: '36px',
              ml: '8px',
              opacity: 1,
              width: '47px',
              '&.Mui-disabled': {
                backgroundColor: '#E8E8EA',
                opacity: 1,
              },
              '&:hover': {
                backgroundColor: isCurrent ? '#3F5724' : isCompleted ? '#DDE5E0' : '#E8E8EA',
              },
            }}
          >
            {isCompleted ? (
              <EditOutlinedIcon sx={{ color: activeColor, fontSize: 22 }} />
            ) : (
              <ArrowForwardIcon sx={{ color: isCurrent ? '#FFFFFF' : '#FFFFFF', fontSize: 22 }} />
            )}
          </IconButton>
        </ListItemButton>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...boxProps.sx,
      }}
    >
      <ListItemButton
        onClick={() => {
          if (data.status === 'completed' || data.status === 'inprogress') onItemClick(data)
        }}
        sx={{ p: isMalaysiaStyle ? '10px 0' : 1.5, m: isMalaysiaStyle ? 0 : -1.5 }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          {data.status === 'completed' || data.status === 'disabled' ? (
            <Box
              sx={{
                height: '40px',
                width: '40px',
                borderRadius: '20px',
                border: 1,
                alignContent: 'center',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                fontSize: '15px',
                fontWeight: '500',
                display: 'flex',
                color: data.status === 'completed' ? activeColor : AppTextDisabled,
                borderColor: data.status === 'completed' ? activeColor : connectorColor,
              }}
            >
              {data.status === 'completed' ? (
                <CircleGreenCheck
                  style={{
                    height: '30px',
                    width: '30px',
                  }}
                />
              ) : (
                <CircleGrayCheck
                  style={{
                    height: '30px',
                    width: '30px',
                  }}
                />
              )}
            </Box>
          ) : (
            <Box
              sx={{
                height: '40px',
                width: '40px',
                borderRadius: '20px',
                border: 1,
                alignContent: 'center',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                borderStyle: data.status === 'inprogress' ? 'dashed' : 'normal',
                display: 'flex',
                color: data.status === 'inprogress' ? activeColor : connectorColor,
                borderColor: data.status === 'inprogress' ? activeColor : connectorColor,
              }}
            >
              <Typography
                sx={{
                  color: data.status === 'inprogress' ? activeTextColor : inactiveTextColor,
                  fontFamily: isMalaysiaStyle ? malaysiaFont : undefined,
                  fontSize: isMalaysiaStyle ? '16px' : '15px',
                  fontWeight: '500',
                  letterSpacing: isMalaysiaStyle ? '0.5px' : undefined,
                  lineHeight: isMalaysiaStyle ? '24px' : undefined,
                }}
              >
                {data.order}
              </Typography>
            </Box>
          )}

          <Typography
            sx={{
              fontSize: isMalaysiaStyle ? '16px' : '15px',
              fontWeight: data.status === 'inprogress' ? '600' : '400',
              fontFamily: isMalaysiaStyle ? malaysiaFont : undefined,
              ml: '16px',
              color: data.status === 'inprogress' ? activeTextColor : inactiveTextColor,
              letterSpacing: isMalaysiaStyle ? '0.5px' : undefined,
              lineHeight: isMalaysiaStyle ? '24px' : undefined,
            }}
          >
            {data.label}
          </Typography>
        </Box>
      </ListItemButton>

      {isLast !== true && (
        <Box
          sx={{
            height: '20px',
            width: '1px',
            ml: '20px',
            alignContent: 'center',
            alignItems: 'center',
            justifyContent: 'center',
            border: 1,
            color: data.status === 'completed' ? activeColor : connectorColor,
            borderStyle: data.status === 'completed' ? 'normal' : 'dashed',
          }}
        />
      )}
    </Box>
  )
}
